/** Wall-mounted steel electrical cabinet for the service corridor. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const part = new THREE.Mesh(geometry, material);
  part.name = name;
  part.castShadow = true;
  part.receiveShadow = true;
  return part;
}

function build(): THREE.Group {
  // Like wall-vent, the face points toward +Z and the group carries no placement
  // rotation. Its lower edge is y=0, including the short conduit, so wall code
  // can place the completed unit on a datum without a cabinet-specific offset.
  const group = new THREE.Group();
  group.name = 'junction-cabinet';

  const steel = surface('metal', 0x778183);
  const darkSteel = surface('metal', 0x4d5758);
  const labelMaterial = surface('mineral', 0xb7bbb6);

  // The 0.80 m cabinet starts above the conduit; the connector is deliberately
  // included in the declared 0.95 m assembly height instead of hiding a false
  // bounding-box claim behind the word "footprint".
  const enclosure = mesh(new THREE.BoxGeometry(0.62, 0.80, 0.19), steel, 'steel-enclosure');
  enclosure.position.set(0, 0.55, -0.015);
  group.add(enclosure);

  const door = new THREE.Group();
  door.name = 'cabinet-door';
  const recessedPanel = mesh(new THREE.BoxGeometry(0.54, 0.69, 0.012), darkSteel, 'door-recess');
  recessedPanel.position.set(0, 0.55, 0.087);
  const frameParts: readonly [readonly [number, number, number], readonly [number, number, number], string][] = [
    [[0.58, 0.025, 0.018], [0, 0.9175, 0.096], 'door-frame-top'],
    [[0.58, 0.025, 0.018], [0, 0.1825, 0.096], 'door-frame-bottom'],
    [[0.025, 0.71, 0.018], [-0.2775, 0.55, 0.096], 'door-frame-left'],
    [[0.025, 0.71, 0.018], [0.2775, 0.55, 0.096], 'door-frame-right'],
  ];
  for (const [size, position, name] of frameParts) {
    const frame = mesh(new THREE.BoxGeometry(...size), steel, name);
    frame.position.set(...position);
    door.add(frame);
  }
  door.add(recessedPanel);
  group.add(door);

  const hardware = new THREE.Group();
  hardware.name = 'door-hinges-and-latch';
  for (const [y, name] of [[0.40, 'hinge-lower'], [0.70, 'hinge-upper']] as const) {
    const hinge = mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.09, 8), darkSteel, name);
    hinge.rotation.x = Math.PI / 2;
    hinge.position.set(-0.285, y, 0.065);
    hardware.add(hinge);
  }
  const latchBase = mesh(new THREE.CylinderGeometry(0.027, 0.027, 0.012, 8), darkSteel, 'quarter-turn-latch-base');
  latchBase.rotation.x = Math.PI / 2;
  latchBase.position.set(0.245, 0.55, 0.104);
  const latchTurn = mesh(new THREE.BoxGeometry(0.07, 0.016, 0.012), darkSteel, 'quarter-turn-latch-handle');
  latchTurn.position.set(0.245, 0.55, 0.104);
  hardware.add(latchBase, latchTurn);
  group.add(hardware);

  const label = mesh(new THREE.BoxGeometry(0.16, 0.09, 0.006), labelMaterial, 'blank-label-plate');
  label.position.set(-0.05, 0.68, 0.107);
  group.add(label);

  const cable = new THREE.Group();
  cable.name = 'bottom-conduit';
  const gland = mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.035, 8), darkSteel, 'cable-gland');
  gland.position.set(0.10, 0.16, -0.015);
  const conduit = mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.15, 8), darkSteel, 'conduit-run');
  conduit.position.set(0.10, 0.075, -0.015);
  cable.add(gland, conduit);
  group.add(cable);

  return group;
}

export const junctionCabinetAsset: AssetDefinition = {
  id: 'junction-cabinet',
  label: 'Junction cabinet',
  description: 'Wall-mounted grey steel electrical cabinet with plain door hardware and conduit.',
  // The specified cabinet box is 0.62 × 0.80 × 0.22 m; this footprint also
  // truthfully includes the required 0.15 m conduit below it.
  footprint: { width: 0.62, height: 0.95, length: 0.22 },
  create: build,
};
